import { DREAM_COMPONENTS_Z_INDEX_VALUES } from '@/constants';
import type { NavbarMenuElementProps } from '@/navbarElement/Navbar.type';
import { cn, getBackgroundColor } from '@/utils';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import * as styles from '../Navbar.css';
import { NAVBAR_MENU_DEFAULTS_V2 } from './NavbarMenu.constants';

const DEFAULT_BOX_SHADOW = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';

export function NavbarMenuV2(props: NavbarMenuElementProps) {
  const { element, children, className, attributes, isInEditor } = props;
  const {
    background,
    shadow,
    style,
    customAttributes,
    borderWidth,
    borderStyle,
    borderColor,
    stickToTop,
    stickyIsland,
    borderRadius,
    stickyBlurBackground,
    stickyBlurOpacity,
    stickyPadding,
    stickyMaxWidth,
    stickyBorderRadius,
  } = element.attrs || {};

  const navStyle = useMemo(() => {
    const resultingBorderRadius = stickToTop
      ? (stickyBorderRadius ?? NAVBAR_MENU_DEFAULTS_V2.stickyBorderRadius)
      : (borderRadius ?? NAVBAR_MENU_DEFAULTS_V2.borderRadius);

    return {
      width: '100%',
      ...(style === 'border'
        ? {
            borderColor: borderColor ?? NAVBAR_MENU_DEFAULTS_V2.borderBorderColor,
            borderWidth: borderWidth ?? NAVBAR_MENU_DEFAULTS_V2.borderBorderWidth,
            borderStyle: borderStyle ?? NAVBAR_MENU_DEFAULTS_V2.borderBorderStyle,
          }
        : {}),
      '--box-shadow': style === 'shadow' ? shadow || DEFAULT_BOX_SHADOW : 'none',
      '--background': getBackgroundColor(background ?? NAVBAR_MENU_DEFAULTS_V2.background),
      '--border-radius': resultingBorderRadius,
      ...(element.content.length === 2 || (Array.isArray(children) && children.length === 2)
        ? {
            '--list-1-border-radius': `${resultingBorderRadius} ${resultingBorderRadius} 0 0`,
            '--list-2-border-radius': `0 0 ${resultingBorderRadius} ${resultingBorderRadius}`,
          }
        : { '--list-1-border-radius': resultingBorderRadius }),
      flexDirection: 'column',
      ...(stickToTop && {
        maxWidth: stickyIsland ? (stickyMaxWidth ?? NAVBAR_MENU_DEFAULTS_V2.stickyMaxWidth) : '100%',
        '--blur-background': stickyBlurBackground ? '80px' : `${NAVBAR_MENU_DEFAULTS_V2.stickyBlurBackground}px`,
        '--blur-opacity': stickyBlurOpacity ? `${Number(stickyBlurOpacity) / 100}` : '0.3',
      }),
    } as CSSProperties & Record<string, any>;
  }, [
    background,
    borderColor,
    borderRadius,
    borderStyle,
    borderWidth,
    shadow,
    style,
    stickToTop,
    stickyBlurBackground,
    stickyBlurOpacity,
    stickyBorderRadius,
    stickyIsland,
    stickyMaxWidth,
    element?.content?.length || 0,
    children,
  ]);

  const wrapperStyle = useMemo(() => {
    if (!(stickToTop && stickyIsland)) return undefined;

    return {
      position: isInEditor ? 'relative' : 'fixed',
      ...(isInEditor ? { width: '100%' } : {}),
      left: 0,
      right: 0,
      padding: stickyPadding ?? '0px',
      zIndex: DREAM_COMPONENTS_Z_INDEX_VALUES.stickyNavbarIslandWrapper,
      display: 'flex',
      justifyContent: 'center',
    } as CSSProperties;
  }, [stickToTop, stickyIsland, stickyPadding, isInEditor]);

  const NavEl = (
    <nav
      {...props}
      role="presentation"
      {...customAttributes}
      className={cn(
        className,
        customAttributes?.className,
        stickToTop && !isInEditor && styles.navbarMenuStickyPlacementV2,
        styles.navbarMenuV2,
        !stickToTop && styles.navigationMenuRelativeV2,
        !isInEditor && !stickToTop && styles.navigationMenuRelativeMinWidthV2,
        stickToTop && styles.navbarMenuBackgroundV2,
      )}
      style={navStyle}
      {...attributes}
    >
      {children}
    </nav>
  );

  if (wrapperStyle) {
    return <div style={wrapperStyle}>{NavEl}</div>;
  }

  return NavEl;
}
